<template>
  <div class="athlete-page people-workspace">
    <header class="people-heading">
      <div>
        <h1>运动员管理</h1>
        <p>{{ isAdmin ? '注册账号与运动员档案' : '本赛事报名成员' }}<span class="heading-divider">/</span><strong>{{ loading ? '—' : total }}</strong> {{ isAdmin ? '人' : '名成员' }}<span v-if="hasFilters" class="filtered-note"> · 当前筛选</span></p>
      </div>
      <a-button v-if="isAdmin" type="primary" icon="plus" @click="openEdit(null)">新增运动员</a-button>
    </header>
    <admin-filters class="page-toolbar" :active="hasFilters">
      <div class="toolbar-left">
        <a-input v-model="query.keyword" placeholder="搜索姓名 / 手机号" aria-label="搜索姓名或手机号" allow-clear class="athlete-search" @pressEnter="search" @change="query.keyword || search()">
          <a-icon slot="prefix" type="search" />
        </a-input>
        <a-select v-if="isAdmin" v-model="query.userRoleType" allow-clear placeholder="全部身份" aria-label="身份" style="width:130px;" @change="search">
          <a-select-option :value="1">运动员</a-select-option>
          <a-select-option :value="2">领队</a-select-option>
        </a-select>
        <a-select v-if="isAdmin" v-model="query.verified" allow-clear placeholder="全部认证状态" aria-label="认证状态" style="width:150px;" @change="search">
          <a-select-option :value="2">官方认证</a-select-option>
          <a-select-option :value="1">已认证</a-select-option>
          <a-select-option :value="0">未认证</a-select-option>
        </a-select>
        <a-select v-if="isAdmin" v-model="query.status" allow-clear placeholder="全部账号状态" aria-label="账号状态" style="width:150px;" @change="search">
          <a-select-option :value="1">正常</a-select-option>
          <a-select-option :value="0">已禁用</a-select-option>
        </a-select>
        <a-button type="primary" icon="search" @click="search">查询</a-button>
        <a-button v-if="hasFilters" @click="resetFilters">重置</a-button>
      </div>
      <div class="toolbar-right">
        <a-button icon="reload" aria-label="刷新运动员列表" @click="loadData">刷新</a-button>
      </div>
    </admin-filters>

    <a-alert v-if="errorText" class="athlete-error" type="error" show-icon :message="errorText" />
    <a-table class="athlete-table" :columns="columns" :data-source="data" :row-key="record => record.id || record.registrationMemberId" :loading="loading" :scroll="{ x: isAdmin ? 1110 : 780 }"
      :pagination="pagination" :locale="{ emptyText: errorText ? '暂时无法载入，请重试' : hasFilters ? '没有符合条件的运动员，试试调整筛选' : '暂无运动员档案' }" @change="handleTableChange" size="middle">
      <div slot="name" slot-scope="text, record" class="athlete-identity">
        <a-avatar :src="record.avatarUrl" :size="42" :style="avatarStyle(record)">
          {{ (record.realName || record.nickname || record.memberName || '运')[0] }}
        </a-avatar>
        <div class="identity-copy">
          <div class="cell-name">{{ record.realName || record.memberName || record.nickname || '未填写姓名' }}</div>
          <div v-if="record.jiangkNo" class="athlete-number">{{ record.jiangkNo }}</div>
          <div v-else class="cell-sub">{{ isAdmin ? '桨刻号待完善' : record.appUserId ? '已关联账号' : '预录成员' }}</div>
        </div>
      </div>
      <span slot="verified" slot-scope="text, record">
        <a-tag :color="verificationColor(record.authStatus)">{{ verificationText(record.authStatus) }}</a-tag>
      </span>
      <div slot="clubs" slot-scope="text, record" class="club-summary">
        <template v-if="hasClubData(record)">
          <div v-for="club in visibleClubs(record)" :key="club.clubId" class="club-summary-row">
            <span class="club-summary-name" :title="club.clubName">{{ club.clubName || '未命名俱乐部' }}</span>
            <span class="club-role" :class="{ 'is-leader': club.role === 'leader' }">{{ clubRoleText(club.role) }}</span>
          </div>
          <a-button v-if="currentClubs(record).length > 2" type="link" size="small" class="more-clubs" @click="openDetail(record, 'clubs')">另 {{ currentClubs(record).length - 2 }} 家 · 查看全部</a-button>
          <span v-if="!currentClubs(record).length" class="club-empty">未加入俱乐部</span>
        </template>
        <span v-else class="club-empty">俱乐部归属未载入</span>
      </div>
      <span slot="role" slot-scope="text, record">
        <a-tag :color="roleColor(record.userRoleType)">{{ roleText(record.userRoleType) }}</a-tag>
      </span>
      <span slot="status" slot-scope="text, record">
        <a-tag :color="record.status === 1 ? 'green' : 'red'">{{ record.status === 1 ? '正常' : '已禁用' }}</a-tag>
      </span>
      <span slot="action" slot-scope="text, record" class="athlete-actions">
        <a-button size="small" @click="openDetail(record)">档案</a-button>
        <template v-if="isAdmin">
          <a-button size="small" type="link" @click="openEdit(record)">编辑</a-button>
          <a-popconfirm title="确定删除该运动员吗？" @confirm="handleDelete(record)">
            <a-button size="small" type="link" class="delete-action">删除</a-button>
          </a-popconfirm>
        </template>
      </span>
    </a-table>

    <!-- 详情抽屉 -->
    <a-drawer :visible="detailVisible" :title="isAdmin ? '运动员档案' : '报名成员档案'" placement="right" width="min(520px, 100vw)" @close="closeDetail">
      <a-spin :spinning="detailLoading">
      <a-alert v-if="detailError" type="error" show-icon :message="detailError" />
      <div v-if="detail">
        <div class="detail-head">
          <a-avatar :src="detail.avatarUrl" :size="64" :style="avatarStyle(detail)">
            {{ (detail.realName || detail.nickname || '运')[0] }}
          </a-avatar>
          <div class="detail-head-info">
            <div class="detail-name">{{ detail.realName || detail.memberName || detail.nickname || '未填写' }}</div>
            <div class="athlete-number">{{ detail.jiangkNo || (detail.appUserId ? '已关联账号' : '—') }}</div>
            <div>
              <a-tag :color="verificationColor(detail.authStatus)">{{ verificationText(detail.authStatus) }}</a-tag>
              <a-tag v-if="detail.userRoleType != null" :color="roleColor(detail.userRoleType)">{{ roleText(detail.userRoleType) }}</a-tag>
            </div>
          </div>
        </div>
        <a-tabs v-model="detailTab">
        <a-tab-pane key="profile" tab="基本资料">
        <div class="detail-body">
          <div v-if="!isAdmin" class="detail-row"><span class="detail-label">赛事 / 队伍</span><span>{{ detail.eventTitle || '—' }} / {{ detail.teamName || '—' }}</span></div>
          <div class="detail-row"><span class="detail-label">联系手机号</span><span>{{ (detail.contactMobile == null ? detail.mobile : detail.contactMobile) || '—' }}</span></div>
          <div class="detail-row"><span class="detail-label">证件号码</span><span>{{ detail.idCardNo || '—' }}</span></div>
          <div class="detail-row"><span class="detail-label">性别</span><span>{{ detail.gender || '—' }}</span></div>
          <div class="detail-row"><span class="detail-label">出生年份</span><span>{{ detail.birthYear || '—' }}</span></div>
          <div class="detail-row"><span class="detail-label">居住地</span><span>{{ provinceText(detail) }}</span></div>
          <div class="detail-row"><span class="detail-label">身高</span><span>{{ detail.heightCm ? detail.heightCm + ' cm' : '—' }}</span></div>
          <div class="detail-row"><span class="detail-label">体重</span><span>{{ detail.weightKg ? detail.weightKg + ' kg' : '—' }}</span></div>
          <div class="detail-row"><span class="detail-label">服装尺码</span><span>{{ detail.clothingSize || '—' }}</span></div>
          <div class="detail-row"><span class="detail-label">参赛位置</span><span>{{ detail.racePosition || '—' }}</span></div>
          <div class="detail-row detail-column">
            <span class="detail-label">参赛经历</span>
            <span class="detail-content">{{ detail.participationHistory || '—' }}</span>
          </div>
          <div v-if="isAdmin" class="detail-row"><span class="detail-label">注册时间</span><span>{{ formatDateTime(detail.createTime) || '—' }}</span></div>
        </div>
        <div v-if="isAdmin" class="detail-foot">
          <a-popconfirm v-if="detail.status === 1" title="确定禁用该账号吗？" @confirm="toggleStatus(detail, 0)">
            <a-button type="danger" block>禁用账号</a-button>
          </a-popconfirm>
          <a-popconfirm v-else title="确定恢复该账号吗？" @confirm="toggleStatus(detail, 1)">
            <a-button type="primary" block>恢复账号</a-button>
          </a-popconfirm>
        </div>
        </a-tab-pane>
        <a-tab-pane v-if="isAdmin" key="clubs" :tab="`所属俱乐部${hasClubData(detail) ? '（' + currentClubs(detail).length + '）' : ''}`">
          <p class="club-detail-note">仅显示已审核通过、仍有效的俱乐部关系。</p>
          <a-alert v-if="!hasClubData(detail)" type="warning" message="俱乐部归属未载入，请重新打开档案" />
          <a-empty v-else-if="!currentClubs(detail).length" description="暂无有效俱乐部关系" :image="false" />
          <div v-else class="club-detail-list">
            <section v-for="club in currentClubs(detail)" :key="club.clubId" class="club-detail-card">
              <div class="club-detail-icon"><a-icon type="team" /></div>
              <h3>{{ club.clubName || '未命名俱乐部' }}</h3>
              <span class="club-role" :class="{ 'is-leader': club.role === 'leader' }">{{ clubRoleText(club.role) }}</span>
            </section>
          </div>
        </a-tab-pane>
        <a-tab-pane v-if="isAdmin" key="account" tab="账号">
          <a-alert v-if="dossierError" type="warning" :message="dossierError" />
          <template v-if="dossier">
            <div class="detail-row"><span class="detail-label">桨刻号</span><span>{{ detail.jiangkNo || '—' }}</span><a-button v-if="dossier.canCorrectJiangkNo" size="small" @click="openNoCorrection">更正桨刻号</a-button></div>
            <div class="detail-row"><span class="detail-label">登录手机号</span><span>{{ dossier.phoneBound ? dossier.phoneMasked : '未验证绑定' }}</span></div>
            <div class="detail-row"><span class="detail-label">微信</span><span>{{ dossier.wechatBound ? '已绑定' : '未绑定' }}</span></div>
            <p class="club-detail-note">联系手机号不等于登录绑定。绑定及换绑由本人在「账号与安全」完成；管理员不能代填验证码或设置登录凭据。</p>
          </template>
        </a-tab-pane>
        <a-tab-pane v-if="isAdmin" key="verification" tab="实名">
          <p class="club-detail-note">档案集中展示审核状态；证件原件仍由实名认证审核权限控制。已认证姓名和证件需通过撤销、重新核验更正。</p>
          <a-button v-if="dossier && dossier.canReview" @click="$router.push('/dragonboat/auth/verifications')">进入实名审核</a-button>
          <a-list v-if="dossier" :data-source="dossier.verificationHistory" size="small">
            <a-list-item slot="renderItem" slot-scope="item"><div>{{ verificationRecordText(item.status) }} · {{ item.documentType || '证件' }}<br><small>提交：{{ formatDateTime(item.submittedAt) || '—' }}　有效期：{{ item.expiresAt || '—' }}</small></div></a-list-item>
          </a-list>
          <a-alert v-if="dossierError" type="warning" :message="dossierError" />
        </a-tab-pane>
        <a-tab-pane v-if="isAdmin" key="events" tab="赛事">
          <p class="club-detail-note">报名、参与认领分别保留，不把认领当作正式报名或官方认证。各展示最近 50 条记录。</p>
          <a-list v-if="dossier" :data-source="dossier.events" size="small"><a-list-item slot="renderItem" slot-scope="item">{{ item.eventTitle }} · 报名{{ registrationText(item.registrationStatus) }}</a-list-item></a-list>
          <a-list v-if="dossier" :data-source="dossier.participation" size="small"><a-list-item slot="renderItem" slot-scope="item">{{ item.eventTitle }} · 参与认领{{ claimText(item.status) }}</a-list-item></a-list>
          <a-alert v-if="dossierError" type="warning" :message="dossierError" />
        </a-tab-pane>
        <a-tab-pane v-if="isAdmin" key="audit" tab="记录">
          <p class="club-detail-note">最近 50 条账号安全及档案访问记录；不包含验证码、完整手机号和证件原件。</p>
          <a-list v-if="dossier" :data-source="dossier.auditHistory" size="small"><a-list-item slot="renderItem" slot-scope="item"><div>{{ item.summary }}<br><small>{{ formatDateTime(item.createdAt) }} · {{ item.actorType === 'ADMIN' ? '管理员' : '本人' }}</small></div></a-list-item></a-list>
          <a-alert v-if="dossierError" type="warning" :message="dossierError" />
        </a-tab-pane>
        </a-tabs>
      </div>
      </a-spin>
    </a-drawer>
    <a-modal :visible="noCorrectionVisible" title="超级管理员更正桨刻号" :confirm-loading="correctingNo" :mask-closable="false" ok-text="确认更正" @ok="submitNoCorrection" @cancel="!correctingNo && (noCorrectionVisible = false)">
      <a-alert type="warning" show-icon message="只更正对外编号，不更换账号。旧号永久保留追溯，不再分配给任何人。已制作的实体卡、二维码等请另行核对。" />
      <a-form layout="vertical" style="margin-top:16px">
        <a-form-item label="当前桨刻号"><a-input :value="noCorrection.expectedNo" disabled /></a-form-item>
        <a-form-item label="新桨刻号" required><a-input v-model="noCorrection.newNo" :max-length="8" placeholder="JK + 6位英文字母或数字" /></a-form-item>
        <a-form-item label="更正原因" required><a-textarea v-model="noCorrection.reason" :max-length="200" :rows="3" placeholder="请填写原因（3至200字），将记录操作人和前后号码" /></a-form-item>
      </a-form>
    </a-modal>
    <!-- 新增/编辑弹窗 -->
    <a-modal :visible="editVisible" :title="editForm.id ? '编辑运动员' : '新增运动员'" width="640" @ok="submitEdit" @cancel="editVisible = false" :confirmLoading="saving">
      <a-form-model :model="editForm" :label-col="{ span: 6 }" :wrapper-col="{ span: 16 }">
        <a-row>
          <a-col :span="12">
            <a-form-model-item label="头像">
          <div style="display:flex; align-items:center; gap:12px;">
            <a-avatar :src="editForm.avatarUrl" :size="48">{{ (editForm.realName || '运')[0] }}</a-avatar>
            <a-upload :customRequest="handleAvatarUpload" :showUploadList="false" accept=".jpg,.jpeg,.png">
              <a-button size="small">上传头像</a-button>
            </a-upload>
          </div>
        </a-form-model-item>
        <a-form-model-item label="桨刻号"><a-input v-model="editForm.jiangkNo" disabled placeholder="自动生成" /></a-form-model-item>
        <a-form-model-item label="姓名" required><a-input v-model="editForm.realName" /></a-form-model-item>
          </a-col>
          <a-col :span="12">
            <a-form-model-item label="性别">
              <a-select v-model="editForm.gender" allow-clear placeholder="选择性别">
                <a-select-option value="男">男</a-select-option>
                <a-select-option value="女">女</a-select-option>
              </a-select>
            </a-form-model-item>
          </a-col>
        </a-row>
        <a-row>
          <a-col :span="12">
            <a-form-model-item label="联系手机号"><phone-input v-model="editForm.mobile" /><small>仅更新联系信息，不更换登录号码</small></a-form-model-item>
          </a-col>
          <a-col :span="12">
            <a-form-model-item label="出生日期">
              <a-date-picker v-model="editForm.birthDate" placeholder="选择出生日期" style="width:100%;" format="YYYY-MM-DD" value-format="YYYY-MM-DD" :allow-clear="true" />
            </a-form-model-item>
          </a-col>
        </a-row>
        <a-form-model-item label="身份证件">
          <template v-if="editForm.id"><span>{{ editForm.idCardNo || '未填写' }}</span><p class="club-detail-note">证件仅显示脱敏信息；请通过实名认证审核流程核验或更正。</p></template>
          <template v-else><a-select v-model="editForm.idCardType"><a-select-option v-for="type in cardTypes" :key="type.value" :value="type.value">{{type.label}}</a-select-option></a-select><a-input v-model="editForm.idCardNo" :max-length="32" placeholder="证件号码（选填，填写不代表认证）" /></template>
        </a-form-model-item>
        <a-form-model-item label="证件原件">
          <span style="color:#999;">请在“实名认证”审核流程中查看和管理，运动员档案不直接保存证件图片。</span>
        </a-form-model-item>
        <a-row>
          <a-col :span="12">
            <a-form-model-item label="身高(cm)"><a-input-number v-model="editForm.heightCm" :min="100" :max="250" style="width:100%;" /></a-form-model-item>
          </a-col>
          <a-col :span="12">
            <a-form-model-item label="体重(kg)"><a-input-number v-model="editForm.weightKg" :min="30" :max="200" style="width:100%;" /></a-form-model-item>
          </a-col>
        </a-row>
        <a-row>
          <a-col :span="12">
            <a-form-model-item label="服装尺码"><a-input v-model="editForm.clothingSize" placeholder="如 S/M/L/XL" /></a-form-model-item>
          </a-col>
          <a-col :span="12">
            <a-form-model-item label="居住地">
              <a-cascader v-model="editForm.region" :options="regionOptions" change-on-select placeholder="选择地区" style="width:100%;" />
            </a-form-model-item>
          </a-col>
        </a-row>
        <a-form-model-item v-if="editForm.region && editForm.region[0] === '海外'" label="海外所在地"><a-input v-model="editForm.residenceCity" placeholder="国家或地区" /><a-input v-model="editForm.residenceDistrict" placeholder="城市（选填）" /></a-form-model-item>
        <a-form-model-item label="个人简介"><a-textarea v-model="editForm.bio" :rows="2" placeholder="选填" /></a-form-model-item>
        <a-form-model-item label="参赛经历">
          <a-textarea v-model="editForm.participationHistory" :rows="3" placeholder="过往参赛及成绩" />
        </a-form-model-item>
      </a-form-model>
    </a-modal>
  </div>
</template>

<script>
import chinaPca from '@/utils/china-pca'
import PhoneInput from '@/components/PhoneInput.vue'
const {cardTypes, documentType, validDocument}=require('../../../../../wx/utils/documents')
const {validPhone}=require('../../../../../wx/utils/phone')
import { deleteDragonAthlete, getDragonAthleteDetail, getDragonAthleteList, saveDragonAthlete, updateDragonAthleteStatus } from '@/api'
import { formatDateTime } from '@/utils/date'
import request from '@/utils/request'

export default {
  name: 'AthleteManage',
  components: {PhoneInput},
  data() {
    return {
      cardTypes,
      isAdmin: false,
      birthYearOptions: (() => {
        const now = new Date().getFullYear()
        const arr = []
        for (let y = now; y >= now - 100; y -= 1) arr.push(y)
        return arr
      })(),
      regionOptions: Object.keys(chinaPca).map(province => ({
        value: province,
        label: province,
        children: province === '海外' ? undefined : Object.keys(chinaPca[province] || {}).map(city => ({
          value: city,
          label: city,
          children: (chinaPca[province][city] || []).map(district => ({ value: district, label: district }))
        }))
      })),
      query: { keyword: undefined, userRoleType: undefined, verified: undefined, status: undefined },
      data: [],
      total: 0,
      loading: false,
      errorText: '',
      loadSerial: 0,
      detailVisible: false,
      detail: null,
      dossier: null,
      dossierError: '',
      noCorrectionVisible: false,
      correctingNo: false,
      noCorrection: {},
      detailTab: 'profile',
      detailLoading: false,
      detailError: '',
      detailSerial: 0,
      editVisible: false,
      editForm: {},
      saving: false,
      pagination: { current: 1, pageSize: 10, total: 0, showSizeChanger: true, showTotal: total => `共 ${total} 人` }
    }
  },
  computed: {
    hasFilters() { return Object.values(this.query).some(value => value != null && value !== '') },
    columns() {
      return [
        { title: '运动员 / 桨刻号', key: 'user', width: 210, scopedSlots: { customRender: 'name' } },
        ...(!this.isAdmin ? [
          { title: '赛事', dataIndex: 'eventTitle', width: 200, customRender: text => text || '—' },
          { title: '队伍', dataIndex: 'teamName', width: 160, customRender: text => text || '—' }
        ] : [
          { title: '所属俱乐部', key: 'clubs', width: 240, scopedSlots: { customRender: 'clubs' } },
          { title: '实名认证', dataIndex: 'authStatus', width: 110, scopedSlots: { customRender: 'verified' } }
        ]),
        { title: '联系手机号', dataIndex: 'mobile', width: 150, customRender: (value,record) => this.maskMobile(record.contactMobile == null ? value : record.contactMobile) },
        ...(this.isAdmin ? [
          { title: '注册时间', dataIndex: 'createTime', width: 155, customRender: text => formatDateTime(text) || '—' },
          { title: '账号', dataIndex: 'status', width: 90, scopedSlots: { customRender: 'status' } }
        ] : []),
        { title: '操作', key: 'action', width: this.isAdmin ? 170 : 100, scopedSlots: { customRender: 'action' } }
      ]
    }
  },
  created() {
    this.loadData()
  },
  methods: {
    hasClubData(record) { return !!record && Array.isArray(record.clubAffiliations) },
    currentClubs(record) { return this.hasClubData(record) ? record.clubAffiliations : [] },
    visibleClubs(record) { return this.currentClubs(record).slice(0, 2) },
    clubRoleText(role) { return role === 'leader' ? '领队' : role === 'member' ? '队员' : '身份待确认' },
    verificationText(status) {
      if (status == null) return '未提供认证状态'
      return Number(status) >= 2 ? '官方认证' : Number(status) === 1 ? '已实名' : '未实名'
    },
    verificationColor(status) { return Number(status) >= 2 ? 'gold' : Number(status) === 1 ? 'green' : undefined },
    maskMobile(value) {
      const text = String(value || '')
      return text.length >= 7 ? `${text.slice(0, 3)}****${text.slice(-4)}` : text ? '已填写' : '未绑定'
    },
    search() { this.pagination.current = 1; this.loadData() },
    resetFilters() { this.query = { keyword: undefined, userRoleType: undefined, verified: undefined, status: undefined }; this.search() },
    avatarStyle(record) {
      return { background: this.avatarColor(record), color: '#fff', fontSize: '16px' }
    },
    avatarColor(record) {
      const name = record.realName || record.nickname || '运'
      const colors = ['#4ECDC4', '#5B8DEF', '#F5A623', '#9254DE', '#52C41A', '#FF7875']
      let sum = 0
      for (let i = 0; i < name.length; i++) sum += name.charCodeAt(i)
      return colors[sum % colors.length]
    },
    roleText(type) {
      if (type === 2 || type === 3) return '领队'
      if (type === 1) return '运动员'
      return '未加入'
    },
    roleColor(type) {
      if (type === 2 || type === 3) return 'blue'
      if (type === 1) return 'cyan'
      return undefined
    },
    provinceText(detail) {
      return [detail.residenceProvince, detail.residenceCity, detail.residenceDistrict].filter(Boolean).join('') || '—'
    },
    formatDateTime,
    loadData() {
      const serial = ++this.loadSerial
      this.loading = true
      this.errorText = ''
      getDragonAthleteList({
        page: this.pagination.current,
        pageSize: this.pagination.pageSize,
        keyword: this.query.keyword,
        userRoleType: this.query.userRoleType,
        verified: this.query.verified,
        status: this.query.status
      }).then(res => {
        if (serial !== this.loadSerial) return
        this.data = res.data.list
        this.total = res.data.total
        this.isAdmin = res.data.scope === 'all'
        this.pagination.total = res.data.total
      }).catch(() => {
        if (serial === this.loadSerial) { this.data = []; this.total = 0; this.pagination.total = 0; this.errorText = '运动员列表载入失败，请点击刷新重试' }
      }).finally(() => {
        if (serial === this.loadSerial) this.loading = false
      })
    },
    handleTableChange(pagination) {
      this.pagination.current = pagination.current
      this.pagination.pageSize = pagination.pageSize
      this.loadData()
    },
    closeDetail() { this.detailSerial += 1; this.detailVisible = false; this.detail = null; this.dossier = null; this.detailLoading = false; this.noCorrectionVisible = false },
    openNoCorrection() {
      if (!this.detail || !this.dossier || !this.dossier.canCorrectJiangkNo) return
      this.noCorrection = {id: this.detail.id, expectedNo: this.detail.jiangkNo, newNo: '', reason: ''}
      this.noCorrectionVisible = true
    },
    submitNoCorrection() {
      if (this.correctingNo) return
      const body = {...this.noCorrection, newNo: (this.noCorrection.newNo || '').trim().toUpperCase(), reason: (this.noCorrection.reason || '').trim()}
      if (!/^JK[0-9A-Z]{6}$/.test(body.newNo)) { this.$message.warning('请输入JK加6位英文字母或数字'); return }
      if (body.newNo === body.expectedNo || body.reason.length < 3) { this.$message.warning('请填写不同的新号码及完整更正原因'); return }
      this.correctingNo = true
      return request({url:'/dragonboat/athlete/jiangk-no/correct', method:'post', data:body}).then(() => {
        this.noCorrectionVisible = false
        this.$message.success('桨刻号已更正，原账号及业务记录保持不变')
        this.loadData()
        if (this.detail && this.detail.id === body.id) return this.openDetail({id:body.id}, 'account')
      }).finally(() => { this.correctingNo = false })
    },
    verificationRecordText(value) { return ({0:'待审核',1:'已通过',2:'已驳回',3:'已撤销',4:'已过期'})[value] || '待核对' },
    registrationText(value) { return ({0:'待审核',1:'已通过',2:'已驳回'})[value] || '待核对' },
    claimText(value) { return ({PENDING:'待确认',CONFIRMED:'已确认',REJECTED:'已驳回',WITHDRAWN:'已撤回',REVOKED:'已撤销'})[value] || '待核对' },
    openDetail(record, tab = 'profile') {
      const serial = ++this.detailSerial
      this.detailTab = tab
      this.detailError = ''
      this.detail = null
      this.dossier = null
      this.dossierError = ''
      this.detailVisible = true
      // Event operators see only their existing registration snapshot, not the admin-only account API.
      if (!this.isAdmin) { this.detailTab = 'profile'; this.detail = { ...record }; this.detailLoading = false; return }
      this.detailLoading = true
      return getDragonAthleteDetail({ id: record.id }).then(res => {
        if (serial !== this.detailSerial) return
        this.detail = res.data
        return request({url: '/dragonboat/athlete/dossier', method: 'get', params: {id: record.id}}).then(result => {
          if (serial === this.detailSerial) this.dossier = result.data
        }).catch(() => { if (serial === this.detailSerial) this.dossierError = '账号关联资料暂未载入，请关闭档案后重试' })
      }).catch(() => { if (serial === this.detailSerial) this.detailError = '档案载入失败，请关闭后重试' })
        .finally(() => { if (serial === this.detailSerial) this.detailLoading = false })
    },
    toggleStatus(record, status) {
      updateDragonAthleteStatus({ id: record.id, status }).then(() => {
        this.$message.success(status === 1 ? '账号已恢复' : '账号已禁用')
        this.loadData()
        if (this.detail && this.detail.id === record.id) {
          this.detail.status = status
        }
      })
    },
    openEdit(record) {
      if (record) {
        this.editForm = {
          id: record.id,
          realName: record.realName || '',
          mobile: record.contactMobile == null ? (record.mobile || '') : record.contactMobile,
          idCardNo: record.idCardNo || '', idCardType: documentType(record.idCardType,record.idCardNo),
          gender: record.gender || undefined,
          birthYear: record.birthYear || undefined,
          birthDate: record.birthDate || (record.birthYear ? `${record.birthYear}-01-01` : null),
          avatarUrl: record.avatarUrl || '',
          jiangkNo: record.jiangkNo || '',
          bio: record.bio || '',
          verified: !!record.verified,
          region: [record.residenceProvince, record.residenceCity, record.residenceDistrict].filter(Boolean),
          residenceProvince: record.residenceProvince || '',
          residenceCity: record.residenceCity || '',
          residenceDistrict: record.residenceDistrict || '',
          heightCm: record.heightCm || undefined,
          weightKg: record.weightKg || undefined,
          clothingSize: record.clothingSize || '',
          participationHistory: record.participationHistory || ''
        }
      } else {
        this.editForm = {
          realName: '',
          mobile: '',
          idCardNo: '', idCardType: 'mainland',
          gender: undefined,
          birthYear: undefined,
          birthDate: null,
          region: [],
          avatarUrl: '',
          jiangkNo: '',
          bio: '',
          verified: false,
          residenceProvince: '',
          residenceCity: '',
          residenceDistrict: '',
          heightCm: undefined,
          weightKg: undefined,
          clothingSize: '',
          participationHistory: ''
        }
      }
      this.editVisible = true
    },
    handleAvatarUpload({ file, onSuccess, onError }) {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('businessType', 'user_avatar')
      request({
        url: '/dragonboat/event/file/upload',
        method: 'post',
        data: formData,
        headers: { 'Content-Type': 'multipart/form-data' }
      }).then(res => {
        this.editForm.avatarUrl = res.data.fileUrl
        this.$message.success('头像已上传')
        onSuccess(res.data)
      }).catch(err => {
        this.$message.error((err && err.msg) || '上传失败')
        onError(err)
      })
    },
    submitEdit() {
      const birthDate = this.editForm.birthDate
      if (birthDate) {
        this.editForm.birthYear = Number(String(birthDate).slice(0, 4))
      }
      const region = this.editForm.region || []
      this.editForm.residenceProvince = region[0] || ''
      if(region[0] !== '海外') {
        this.editForm.residenceCity = region[1] || ''
        this.editForm.residenceDistrict = (region[2] || '').replace(/^不填写区县$/, '')
      }
      if (!this.editForm.realName) {
        this.$message.warning('请填写姓名')
        return
      }
      if(this.editForm.mobile && !validPhone(this.editForm.mobile)) {this.$message.warning('请核对区号和手机号码');return}
      if(!this.editForm.id && this.editForm.idCardNo && !validDocument(this.editForm.idCardNo,this.editForm.idCardType)) {this.$message.warning('请核对证件类型和号码');return}
      this.saving = true
      const payload = {...this.editForm,contactMobile:this.editForm.mobile}
      if (payload.id) { delete payload.idCardNo; delete payload.idCardType }
      saveDragonAthlete(payload).then(() => {
        this.$message.success(this.editForm.id ? '运动员资料已更新' : '运动员已建档')
        this.editVisible = false
        this.loadData()
      }).finally(() => {
        this.saving = false
      })
    },
    handleDelete(record) {
      deleteDragonAthlete({ id: record.id }).then(() => {
        this.$message.success('已删除')
        this.loadData()
      })
    }
  }
}
</script>

<style scoped>
.athlete-page { min-height: 65vh; }
.athlete-search { width: 240px; max-width: 100%; }
.athlete-error { margin-bottom: 16px; }
.athlete-table { background: #fff; border: 1px solid #dfe8ed; border-radius: 12px; overflow: hidden; }
.athlete-table /deep/ .ant-table-thead > tr > th { background: #f7fafb; color: #526373; font-size: 14px; font-weight: 600; }
.athlete-table /deep/ .ant-table-tbody > tr > td { padding-top: 18px; padding-bottom: 18px; border-bottom-color: #edf1f4; }
.athlete-table /deep/ .ant-table-pagination { margin-right: 18px; }
.athlete-identity { display: flex; align-items: center; gap: 12px; }
.athlete-identity .ant-avatar { flex-shrink: 0; }
.identity-copy { min-width: 0; }
.athlete-number { margin-top: 3px; color: #526877; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 13px; letter-spacing: .035em; }
.athlete-actions { display: inline-flex; align-items: center; gap: 4px; white-space: nowrap; }
.athlete-actions .delete-action { color: #bd5050; }
.club-summary { display: flex; flex-direction: column; gap: 7px; }
.club-summary-row { display: flex; align-items: center; gap: 8px; min-width: 0; }
.club-summary-name { min-width: 0; flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: 14px; color: #304c5c; }
.club-role { display: inline-block; flex-shrink: 0; padding: 1px 6px; border: 1px solid #e0e7ec; border-radius: 4px; color: #607583; font-size: 12px; line-height: 20px; background: #f7f9fb; }
.club-role.is-leader { color: #176861; background: #eaf7f4; border-color: #cbe6df; }
.club-empty { color: #758692; font-size: 14px; }
.club-summary .more-clubs { align-self: flex-start; padding: 0; min-height: 26px; height: auto; font-size: 13px; }
.club-detail-note { color: #607583; font-size: 14px; line-height: 1.6; margin-bottom: 18px; }
.club-detail-list { display: flex; flex-direction: column; gap: 12px; }
.club-detail-card { display: flex; align-items: center; gap: 12px; padding: 16px; border: 1px solid #e0e8ec; border-radius: 10px; background: #fbfdfd; }
.club-detail-icon { width: 36px; height: 36px; flex-shrink: 0; display: grid; place-items: center; color: #267c76; background: #eef7f5; border-radius: 8px; font-size: 18px; }
.club-detail-card h3 { flex: 1; min-width: 0; overflow-wrap: anywhere; margin: 0; font-size: 16px; line-height: 1.5; color: #233f4e; }

.page-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  gap: 12px;
  flex-wrap: wrap;
}

.toolbar-left {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.total-tip {
  color: #7A8A9A;
  font-size: 13px;
}

.cell-name {
  font-weight: 600;
  font-size: 16px;
  line-height: 1.5;
  color: #16232F;
}

.cell-sub {
  font-size: 12px;
  color: #6b7d8a;
}

.detail-head {
  display: flex;
  align-items: center;
  gap: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid #E4EDF2;
  margin-bottom: 16px;
}

.detail-name {
  font-size: 18px;
  font-weight: 700;
  color: #16232F;
}

.detail-nick {
  font-size: 13px;
  color: #A8B8C2;
  margin-bottom: 6px;
}

.detail-body { margin-bottom: 20px; }

.detail-row {
  display: flex;
  padding: 10px 0;
  border-bottom: 1px dashed #E4EDF2;
  font-size: 14px;
}

.detail-row.detail-column {
  flex-direction: column;
  gap: 6px;
}

.detail-label {
  width: 90px;
  color: #7A8A9A;
  flex-shrink: 0;
}

.detail-content {
  color: #2C3A4B;
  line-height: 1.6;
}
</style>
<style src="@/styles/people-workspace.css"></style>
